import type { SessionResponse } from './auth';

const DEV_MODE_VALUE = 'true';
const STORAGE_KEY_DEV_USER = 'admindashboard_dev_user';
const STORAGE_KEY_DEV_ORG = 'admindashboard_dev_org';
const STORAGE_KEY_DEV_ROLE = 'admindashboard_dev_role';
const STORAGE_KEY_DEV_STAFF = 'admindashboard_dev_staff';
const DEFAULT_DEV_USER = 'dev.user@scion.invalid';
// The admin dashboard operates against the real Parslee org, so dev mode
// defaults to it (mirrors the DEFAULT_ORGANIZATION_ID the old MSAL path used).
const DEFAULT_DEV_ORG = 'org_button_up';
const DEFAULT_DEV_ROLE: DevRole = 'Admin';
const DEV_ROLES = ['Admin', 'Owner', 'Member'] as const;

export type DevRole = typeof DEV_ROLES[number];

export interface DevIdentity {
  userId: string;
  email: string;
  name: string;
  organizationId: string;
  role: DevRole;
  /**
   * Dev-mode Parslee-staff toggle. The admin dashboard is a Parslee-internal
   * tool, so dev mode defaults to staff = TRUE (so a local run isn't stuck on
   * the StaffGate access-denied state out of the box). Set `?devStaff=false`
   * (sticky in localStorage) or `VITE_DEV_STAFF=false` to exercise the
   * non-staff / access-denied path locally.
   */
  isParsleeStaff: boolean;
}

export function isDevModeEnabled(): boolean {
  return import.meta.env.VITE_DEV_MODE === DEV_MODE_VALUE;
}

export function getDevIdentity(): DevIdentity {
  const user = resolveOverride('devUser', STORAGE_KEY_DEV_USER)
    ?? normalize(import.meta.env.VITE_DEV_USER)
    ?? DEFAULT_DEV_USER;
  const organizationId = resolveOverride('devOrg', STORAGE_KEY_DEV_ORG)
    ?? normalize(import.meta.env.VITE_DEV_ORG)
    ?? DEFAULT_DEV_ORG;
  const role = resolveRoleOverride();
  const isParsleeStaff = resolveStaffOverride();

  return {
    userId: user,
    email: user.includes('@') ? user : `${user}@parslee.local`,
    name: displayNameFromUser(user),
    organizationId,
    role,
    isParsleeStaff,
  };
}

export function getDevIdentityHeaders(): Record<string, string> {
  if (!isDevModeEnabled()) {
    return {};
  }

  const identity = getDevIdentity();
  const headers: Record<string, string> = {
    'X-User-Id': identity.userId,
    'X-User-Email': identity.email,
    'X-Organization-Id': identity.organizationId,
    'X-User-Role': identity.role,
  };
  if (identity.isParsleeStaff) {
    headers['X-Is-Parslee-Staff'] = 'true';
  }

  return headers;
}

export function getDevSession(): SessionResponse {
  const identity = getDevIdentity();

  return {
    authenticated: true,
    account: {
      id: identity.userId,
      email: identity.email,
      name: identity.name,
    },
    activeOrganization: identity.organizationId,
    role: identity.role,
    organizations: [
      {
        id: identity.organizationId,
        name: 'ButtonUp',
        role: identity.role,
      },
    ],
    linkedAccounts: [],
    entitledProducts: [],
    isParsleeStaff: identity.isParsleeStaff,
  };
}

export function setDevOrganizationOverride(organizationId: string): void {
  writeStorage(STORAGE_KEY_DEV_ORG, organizationId);
}

export function clearDevIdentityOverrides(): void {
  try {
    localStorage.removeItem(STORAGE_KEY_DEV_USER);
    localStorage.removeItem(STORAGE_KEY_DEV_ORG);
    localStorage.removeItem(STORAGE_KEY_DEV_ROLE);
    localStorage.removeItem(STORAGE_KEY_DEV_STAFF);
  } catch {
    // localStorage may be unavailable.
  }
}

/**
 * Resolves the dev Parslee-staff toggle. Defaults to TRUE (internal tool);
 * only an explicit "false" (via `?devStaff=false`, sticky in localStorage, or
 * `VITE_DEV_STAFF=false`) turns it off so the access-denied path can be tested.
 */
function resolveStaffOverride(): boolean {
  const fromUrl = readUrlParam('devStaff');
  if (fromUrl !== null) {
    const isStaff = fromUrl.toLowerCase() !== 'false';
    writeStorage(STORAGE_KEY_DEV_STAFF, isStaff ? 'true' : 'false');
    return isStaff;
  }

  const fromStorage = readStorage(STORAGE_KEY_DEV_STAFF);
  if (fromStorage !== null) {
    return fromStorage.toLowerCase() !== 'false';
  }

  return normalize(import.meta.env.VITE_DEV_STAFF)?.toLowerCase() !== 'false';
}

function resolveOverride(paramName: string, storageKey: string): string | null {
  const fromUrl = readUrlParam(paramName);
  if (fromUrl) {
    writeStorage(storageKey, fromUrl);
    return fromUrl;
  }

  return readStorage(storageKey);
}

function resolveRoleOverride(): DevRole {
  const fromUrl = readUrlParam('devRole');
  if (fromUrl !== null) {
    const role = normalizeRole(fromUrl);
    if (role) {
      writeStorage(STORAGE_KEY_DEV_ROLE, role);
      return role;
    }

    removeStorage(STORAGE_KEY_DEV_ROLE);
    return DEFAULT_DEV_ROLE;
  }

  return normalizeRole(readStorage(STORAGE_KEY_DEV_ROLE))
    ?? normalizeRole(import.meta.env.VITE_DEV_ROLE)
    ?? DEFAULT_DEV_ROLE;
}

function readUrlParam(name: string): string | null {
  try {
    return normalize(new URLSearchParams(window.location.search).get(name));
  } catch {
    return null;
  }
}

function readStorage(key: string): string | null {
  try {
    return normalize(localStorage.getItem(key));
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // localStorage may be unavailable.
  }
}

function removeStorage(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // localStorage may be unavailable.
  }
}

function normalize(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

function normalizeRole(value: string | null | undefined): DevRole | null {
  const normalized = normalize(value);
  return DEV_ROLES.includes(normalized as DevRole) ? normalized as DevRole : null;
}

function displayNameFromUser(user: string): string {
  const localPart = user.split('@')[0] || user;
  return localPart
    .split(/[._-]+/)
    .filter(Boolean)
    .map((part) => part[0].toUpperCase() + part.slice(1))
    .join(' ') || 'Admin Dashboard Dev User';
}
